import React, { useCallback, useMemo } from "react";
import { useIsMobile } from "../../../utils/useIsMobile";

/**
 * 캠페인 테이블 컴포넌트
 * @param {Object} props
 * @param {Array} props.columns - 컬럼 정의 배열
 * @param {Array} props.data - 테이블 데이터 배열
 * @param {string} props.keyField - 고유 키 필드명
 * @param {Array} props.selectedIds - 선택된 ID 배열
 * @param {Function} props.onSelect - 선택 핸들러
 * @param {Function} props.onSelectAll - 전체 선택 핸들러
 * @param {Object} props.rowClick - 행 클릭 설정 { enabled, handler? }
 */
function CampaignTable({
  columns = [],
  data = [],
  keyField = "id",
  selectedIds = [],
  onSelect,
  onSelectAll,
  rowClick = { enabled: false },
  handlers = {},
}) {
  const isMobile = useIsMobile();

  const handleRowClick = useCallback(
    (row) => {
      if (rowClick.enabled && rowClick.handler) {
        rowClick.handler(row);
      }
    },
    [rowClick],
  );

  const handleSelectAll = useCallback(
    (e) => {
      if (onSelectAll) {
        onSelectAll(e.target.checked);
      }
    },
    [onSelectAll],
  );

  const isAllSelected = useMemo(() => {
    return data.length > 0 && selectedIds.length === data.length;
  }, [data.length, selectedIds.length]);

  if (!columns || columns.length === 0) {
    return <div>컬럼이 정의되지 않았습니다.</div>;
  }

  // 모바일: 행 1개 = 카드 1개("열 이름: 값"). 표와 동시에 렌더링하지 않는다(data-* 위임 대상이 2벌 생김, D-002)
  if (isMobile) {
    return (
      <div className="campaign-card-list">
        {onSelect && data.length > 0 && (
          <div className="campaign-card-toolbar">
            <label className="campaign-card-select-all">
              <input type="checkbox" onChange={handleSelectAll} checked={isAllSelected} />
              전체 선택
            </label>
            <span className="campaign-card-count">
              선택 {selectedIds.length} / 전체 {data.length}
            </span>
          </div>
        )}
        {data.length === 0 ? (
          <div className="campaign-card-empty">데이터가 없습니다.</div>
        ) : (
          data.map((row) => {
            const isSelected = selectedIds.includes(row[keyField]);
            return (
              <div
                key={row[keyField]}
                className={`campaign-card${rowClick.enabled ? " clickable" : ""}${isSelected ? " selected" : ""}`}
                onClick={() => handleRowClick(row)}
              >
                {onSelect && (
                  // 터치 영역 전체가 체크박스를 토글하고, 카드 클릭(행 클릭)으로 번지지 않게 막는다
                  <label className="campaign-card-check" onClick={(e) => e.stopPropagation()}>
                    <input type="checkbox" checked={isSelected} onChange={() => onSelect(row[keyField])} />
                  </label>
                )}
                {columns.map((column) => (
                  <div key={column.key} className="campaign-card-field">
                    <div className="campaign-card-label">{column.label.split("\n").join(" / ")}</div>
                    <div className="campaign-card-value">
                      {column.render ? column.render(row, handlers, column) : row[column.key]}
                    </div>
                  </div>
                ))}
              </div>
            );
          })
        )}
      </div>
    );
  }

  return (
    <div className="campaign-table">
      <table>
        <thead>
          <tr>
            {onSelect && (
              <th className="center-cell">
                <input type="checkbox" onChange={handleSelectAll} checked={isAllSelected} />
              </th>
            )}
            {columns.map((column) => (
              <th key={column.key} className={column.center ? "center-cell" : ""} style={{ width: column.width }}>
                {column.label.includes("\n")
                  ? column.label.split("\n").map((line, idx, arr) => (
                      <React.Fragment key={idx}>
                        {line}
                        {idx < arr.length - 1 && <br />}
                      </React.Fragment>
                    ))
                  : column.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {data.length === 0 ? (
            <tr>
              <td colSpan={columns.length + (onSelect ? 1 : 0)} className="center-cell">
                데이터가 없습니다.
              </td>
            </tr>
          ) : (
            data.map((row) => (
              <tr
                key={row[keyField]}
                className={rowClick.enabled ? "clickable-row" : ""}
                onClick={() => handleRowClick(row)}
                style={{ cursor: rowClick.enabled ? "pointer" : "default" }}
              >
                {onSelect && (
                  <td className="center-cell">
                    <input
                      type="checkbox"
                      checked={selectedIds.includes(row[keyField])}
                      onChange={() => onSelect(row[keyField])}
                      onClick={(e) => e.stopPropagation()}
                    />
                  </td>
                )}
                {columns.map((column) => {
                  const cellContent = column.render ? column.render(row, handlers, column) : row[column.key];

                  return (
                    <td key={column.key} className={column.center ? "center-cell" : ""} style={{ width: column.width }}>
                      {cellContent}
                    </td>
                  );
                })}
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
}

export default React.memo(CampaignTable);
